import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useHistoryFamily } from '@hooks/register/history/use-history-family';
import { useLocalize } from '@hooks/use-localize';
import { useScrollToHash } from '@hooks/use-scroll-to-hash';
import { useStepForm } from '@hooks/use-step-form';
import { asString } from '@/utils/parse';
import { createHistoryFamilySchema } from '@views/register/history/history-family.schema';
import { DashRegistrationStep, historyLinkMap } from '@/types/dashboard';
import type { IHistoryFamilyFormValues, IHistoryFamilyModel, IHistoryFamilySubmitResult } from '@/types/history/history-family';
import ConditionsSection from '@views/register/history/family-sections/conditions-section';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';

const HistoryFamily = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useHistoryFamily(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <HistoryFamilyContent model={model} submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
        </PageLoader>
    );
};

interface IHistoryFamilyContentProps {
    model: IHistoryFamilyModel;
    submit: (values: IHistoryFamilyFormValues) => Promise<IHistoryFamilySubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const HistoryFamilyContent = ({ model, submit, isSubmitting, submitError }: IHistoryFamilyContentProps) => {
    const t = useLocalize(model.languageData);

    useScrollToHash();

    const form = useStepForm<IHistoryFamilyFormValues, IHistoryFamilySubmitResult>({
        // The schema validates a string record; the form values are all strings.
        resolver: zodResolver(
            createHistoryFamilySchema(model),
        ) as unknown as Resolver<IHistoryFamilyFormValues>,
        defaultValues: {
            familyHistoryKidneyDisease: asString(model.familyHistoryKidneyDisease),
            familyMembersKidneyDisease: model.familyMembersKidneyDisease,
            familyHistoryLiverDisease: asString(model.familyHistoryLiverDisease),
            familyMembersLiverDisease: model.familyMembersLiverDisease,
            familyHistoryBowelIssues: asString(model.familyHistoryBowelIssues),
            familyMembersBowelIssues: model.familyMembersBowelIssues,
            familyHistoryLupusLiver: asString(model.familyHistoryLupusLiver),
            familyMembersLupusLiver: model.familyMembersLupusLiver,
            familyHistoryHeartDisease: asString(model.familyHistoryHeartDisease),
            familyMembersHeartDisease: model.familyMembersHeartDisease,
            familyHistoryCad: asString(model.familyHistoryCad),
            familyMembersCad: model.familyMembersCad,
            familyHistoryBloodPressure: asString(model.familyHistoryBloodPressure),
            familyMembersBloodPressure: model.familyMembersBloodPressure,
            familyHistoryBloodDisorder: asString(model.familyHistoryBloodDisorder),
            familyMembersBloodDisorder: model.familyMembersBloodDisorder,
            familyHistoryCancer: asString(model.familyHistoryCancer),
            familyMembersCancer: model.familyMembersCancer,
            familyHistoryKidneyCancer: asString(model.familyHistoryKidneyCancer),
            familyMembersKidneyCancer: model.familyMembersKidneyCancer,
            familyHistoryDiabetes: asString(model.familyHistoryDiabetes),
            familyMembersDiabetes: model.familyMembersDiabetes,
            familyMemberSuddenDeathUnder50: asString(model.familyMemberSuddenDeathUnder50),
            familyMemberSuddenDeathUnder50Description: model.familyMemberSuddenDeathUnder50Description,
        },
        submit,
        linkMap: historyLinkMap,
    });

    return (
        <StepFormLayout
            kind="history"
            model={model}
            step={DashRegistrationStep.HistoryFamily}
            title={t('DashLabel_FamilyHistory', 'Family History')}
            subtitle={
                <h4>
                    {t(
                        'DashLabel_PleaseAnswerTheFollowingQuestionsAsTheyApplyToYour',
                        'Please answer the following questions as they apply to your parents, brothers/sisters, and maternal/paternal grandparents ONLY',
                    )}
                    .
                </h4>
            }
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div id="conditions">
                <ConditionsSection model={model} t={t} />
            </div>
        </StepFormLayout>
    );
};

export default HistoryFamily;
